/**
 * Pure utilities shared between the server (`/leaderboard` REST + the
 * StudyGroupDo `sg-score-delta` handler) and the client (period
 * rendering, summary breakdown). No I/O, no React, no wall-clock reads
 * — every time input is passed in by the caller.
 *
 * ### Centi-MP boundary
 * All MP values handled here are CENTI-MP integers (1 MP = 100 units)
 * — matches the storage convention on `SGScore`. The single place that
 * converts to display MP is `buildLeaderboard` (Math.round(centi/100)
 * just before constructing the response row). Everywhere else stays
 * in centi-MP so the sort comparator + period sums remain integer-exact.
 *
 * ### Client-side score formula
 * `EVENT_POINTS`, `STREAK_MULTIPLIER_TABLE`, and `scoreFor()` live in
 * this same file — the score-formula primitives that compute the
 * per-card centi-MP delta a client pushes. They sit next to the
 * period/bucketing helpers below so client and server agree on one
 * source of truth for "how MP is shaped."
 */

import type { SGMember, SGScore } from '../../sync-engine/schema/group-entities';

/** Leaderboard period buckets — fixed enum used by the REST query param. */
export type LeaderboardPeriod = '1week' | '1month' | '3months' | '6months' | 'alltime';

/**
 * Monday 00:00:00.000Z (UTC) of the ISO week containing `utcAtISO`.
 *
 * Boundary for the server's lazy `weekMP` reset AND for the REST
 * endpoint's `1week` period bucket. JS `getUTCDay()` returns 0..6
 * with Sunday=0; we shift so Monday=0 then walk back by that offset.
 *
 * Throws on an unparseable ISO so a bad payload surfaces loudly at the
 * boundary instead of silently emitting a `NaN`-laced ISO downstream.
 */
export function mondayUTCStartISO(utcAtISO: string): string {
  const d = new Date(utcAtISO);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`mondayUTCStartISO: invalid ISO "${utcAtISO}"`);
  }
  const jsDay = d.getUTCDay(); // 0=Sun..6=Sat
  const offsetFromMonday = (jsDay + 6) % 7; // 0 if Mon, 1 if Tue, ... 6 if Sun.
  const monday = new Date(
    Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate() - offsetFromMonday,
      0,
      0,
      0,
      0,
    ),
  );
  return monday.toISOString();
}

/**
 * Calendar-day diff between two `YYYY-MM-DD` strings, treating each as
 * UTC midnight. Signed: `daysDiffLocal('2026-06-15', '2026-06-14')` ===
 * 1 (yesterday → today is +1 forward).
 *
 * Used by the server's streak-bump branch: `diff === 1` means a
 * consecutive local day → streak += 1; `diff > 1` means a gap → reset
 * to 1. The local-day key is what the user sees as "today", so we
 * never convert through real timezone math — we just parse the
 * `YYYY-MM-DD` triple as UTC midnight and subtract.
 */
export function daysDiffLocal(aISO: string, bISO: string): number {
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (!re.test(aISO) || !re.test(bISO)) {
    throw new Error(`daysDiffLocal: expected YYYY-MM-DD, got "${aISO}" / "${bISO}"`);
  }
  const a = Date.UTC(
    Number(aISO.slice(0, 4)),
    Number(aISO.slice(5, 7)) - 1,
    Number(aISO.slice(8, 10)),
  );
  const b = Date.UTC(
    Number(bISO.slice(0, 4)),
    Number(bISO.slice(5, 7)) - 1,
    Number(bISO.slice(8, 10)),
  );
  return Math.round((a - b) / 86_400_000);
}

/**
 * Streak as it stands TODAY for the given anchor. Stored streak goes
 * stale once the user misses a full day — this collapses to 0 instead
 * of returning the (now-broken) raw count, so:
 *   - The leaderboard shows the correct value to other members the
 *     moment the gap opens, without waiting for the user to push a delta.
 *   - The first card of a resumed session picks the ×1.0 multiplier
 *     (matching the post-bump streak the server will compute), instead
 *     of using the pre-gap inflated multiplier.
 *
 *   diff <= 1 → studied today (0) or yesterday (1) — streak alive.
 *   diff >= 2 → at least one full local day missed — streak broken.
 *   diff <  0 → out-of-order frame (e.g. offline backlog) — keep stored
 *               value; the server's bump logic owns the anchor advance.
 *
 * The reference frame is whatever YYYY-MM-DD the caller passes as
 * `todayLocalDayISO`. The client multiplier path passes the device's
 * actual local day (no TZ fuzz). `buildLeaderboard` passes UTC date,
 * which has ±1-day slop for far-from-UTC viewers — that's tolerable
 * because the next delta self-heals via the server's authoritative
 * bump using the user's real `userLocalDayISO` from the wire.
 */
export function effectiveStreak(
  row: { streak: number; lastStudyLocalDayISO: string } | undefined,
  todayLocalDayISO: string,
): number {
  if (!row || !row.lastStudyLocalDayISO) return 0;
  const diff = daysDiffLocal(todayLocalDayISO, row.lastStudyLocalDayISO);
  if (diff < 0) return row.streak;
  return diff <= 1 ? row.streak : 0;
}

/**
 * Garbage-collect a `monthlyMP` map to the 7 most recent month buckets.
 * Called on every server write so storage never grows unbounded.
 *
 * `YYYY-MM` keys sort lexicographically as chronologically — no Date
 * parsing required. Map ordering inside the returned object follows
 * V8 insertion order (most-recent-last); callers that care about
 * ordering should re-sort themselves.
 */
export function gcMonthlyMP(monthly: Record<string, number>): Record<string, number> {
  const keys = Object.keys(monthly);
  if (keys.length <= 7) return monthly;
  const recent = keys.sort().slice(-7);
  const out: Record<string, number> = {};
  for (const k of recent) out[k] = monthly[k];
  return out;
}

/**
 * Centi-MP total for `row` over `period`, given the current UTC instant
 * `nowUtcISO`. Returns 0 for stale buckets (e.g. a `weekStartISO` that
 * doesn't match the current week — the lazy reset hasn't run yet) and
 * for absent month keys.
 *
 * Stays in centi-MP — `buildLeaderboard` is the one place that
 * converts to display MP.
 *
 * ### Period semantics
 * `1month` / `3months` / `6months` each sum the CURRENT (partial)
 * month bucket PLUS the `n` most recent previous full months. So "1
 * month" covers 30–60 days of activity, "3 months" covers 90–120,
 * etc. Rationale: a Mon–Sun ISO week can straddle a month boundary
 * (e.g. today is Wed July 1 and the user studied Mon June 29). If
 * `1month` used only the current calendar-month bucket, this-week's
 * MP would visibly exceed this-month's MP, which reads as broken. The
 * `n + 1` bucket window ensures monotonic ordering (1week ≤ 1month ≤
 * 3months ≤ 6months ≤ alltime) without needing day-precise storage.
 *
 * ### Alltime robustness
 * Prod `applyScoreDelta` increments `totalMP` and `monthlyMP[key]` in
 * lockstep, so they stay consistent. But the dev-override tool (and
 * any future path that patches counters directly) can leave them out
 * of sync — and when that happens, `sum(monthlyMP) > totalMP` would
 * cause a sub-period to visibly exceed alltime. Take the max so
 * alltime is a strict upper bound regardless of write-path drift.
 */
export function computePeriodMP(
  row: Pick<SGScore, 'totalMP' | 'weekMP' | 'weekStartISO' | 'monthlyMP'>,
  period: LeaderboardPeriod,
  nowUtcISO: string,
): number {
  switch (period) {
    case 'alltime': {
      let sum = 0;
      for (const v of Object.values(row.monthlyMP)) sum += v;
      return Math.max(row.totalMP, sum);
    }
    case '1week': {
      const currentWeek = mondayUTCStartISO(nowUtcISO);
      return row.weekStartISO === currentWeek ? row.weekMP : 0;
    }
    case '1month':
    case '3months':
    case '6months': {
      const n = period === '1month' ? 1 : period === '3months' ? 3 : 6;
      // n + 1 buckets = current (partial) month + n previous full months.
      const recent = mostRecentMonthKeys(nowUtcISO, n + 1);
      let total = 0;
      for (const k of recent) total += row.monthlyMP[k] ?? 0;
      return total;
    }
  }
}

/**
 * Tracked study time (integer ms) for `row` over `period` — the Time
 * ranking's mirror of `computePeriodMP`. Same lazy-bucket semantics:
 * a stale `weekStartISO` (the lazy reset hasn't run yet) and absent
 * month keys both read as 0. The `?? 0` guards additionally cover rows
 * written before the Time feature existed, which lack the fields
 * entirely (they're optional on `SGScore`).
 *
 * Month windows use the SAME `n + 1` bucket rule as `computePeriodMP`
 * (current partial month + `n` previous full months — see its "Period
 * semantics" doc). Anything narrower breaks monotonicity: on the 1st
 * of a month the current bucket is near-empty, so a current-bucket-only
 * "1 month" would rank BELOW "1 week", and the two tabs would disagree
 * on what a period means.
 *
 * Stays in raw ms — the display conversion (`2h 28min`) happens in the
 * UI via `studyTimeDisplayParts`, never here.
 */
export function computePeriodTimeMs(
  row: Pick<SGScore, 'totalTimeMs' | 'weekTimeMs' | 'weekStartISO' | 'monthlyTimeMs'>,
  period: LeaderboardPeriod,
  nowUtcISO: string,
): number {
  switch (period) {
    case 'alltime':
      return row.totalTimeMs ?? 0;
    case '1week': {
      const currentWeek = mondayUTCStartISO(nowUtcISO);
      return row.weekStartISO === currentWeek ? (row.weekTimeMs ?? 0) : 0;
    }
    case '1month':
    case '3months':
    case '6months': {
      const n = period === '1month' ? 1 : period === '3months' ? 3 : 6;
      // n + 1 buckets = current (partial) month + n previous full months.
      const recent = mostRecentMonthKeys(nowUtcISO, n + 1);
      let total = 0;
      for (const k of recent) total += row.monthlyTimeMs?.[k] ?? 0;
      return total;
    }
  }
}

/** Recent `n` months (`YYYY-MM`) ending at the month of `nowUtcISO`, INCLUSIVE. */
function mostRecentMonthKeys(nowUtcISO: string, n: number): string[] {
  const d = new Date(nowUtcISO);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`mostRecentMonthKeys: invalid ISO "${nowUtcISO}"`);
  }
  const out: string[] = [];
  let y = d.getUTCFullYear();
  let m = d.getUTCMonth(); // 0..11
  for (let i = 0; i < n; i++) {
    out.push(`${pad4(y)}-${pad2(m + 1)}`);
    m -= 1;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
  }
  return out;
}

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
const pad4 = (n: number): string =>
  n < 10 ? `000${n}` : n < 100 ? `00${n}` : n < 1000 ? `0${n}` : `${n}`;

export type LeaderboardRow = {
  userId: string;
  rank: number;
  /** DISPLAY MP — centi-MP / 100 then Math.round. */
  mp: number;
  streak: number;
  /** `true` only on the requester's own row when they've opted out. */
  hiddenSelf: boolean;
  displayName?: string;
  avatarUrl?: string;
  /**
   * ISO timestamp of the member's most recent app open (mirrors
   * `SGMember.lastOpenedAtISO`). Consumed by the row avatar tooltip to
   * render the green/amber/grey activity dot via `getActivityStatus`.
   */
  lastActive?: string;
  /**
   * Tracked study time for the selected period, RAW integer ms (clients
   * format via `studyTimeDisplayParts`). The Time tab is an INDEPENDENT
   * ranking — `timeMs` never feeds `mp` or `rank`. Optional for rollout
   * skew: an old server omits it and consumers fall back to 0; the
   * current server always fills it.
   */
  timeMs?: number;
  /**
   * Dense rank within the Time ranking (same dense semantics + tie-breaks
   * as `rank`, sorted by `timeMs` desc). Optional for rollout skew, same
   * contract as `timeMs`.
   */
  timeRank?: number;
};

export type LeaderboardResponse = {
  period: LeaderboardPeriod;
  ranking: LeaderboardRow[];
  /** The requester's own row — always present even when they're hidden. */
  requestor: LeaderboardRow;
};

/**
 * Build a leaderboard response from raw centi-MP score rows + member
 * rows, for the given `requesterUserId`.
 *
 * Rules:
 *   - Drop kicked/left members (`deletedTimeISO != null`).
 *   - Drop `leaderboardOptOut === true` from `ranking[]` EXCEPT for the
 *     requester themselves (`!optOut OR userId === requester`).
 *   - Members without a score row contribute MP = 0, streak = 0 — they
 *     are still ranked (relative to other zero-MP members) by the
 *     deterministic tie-break.
 *   - Sort desc by period MP; tie-break by `lastStudyLocalDayISO` desc
 *     (recent activity wins), then `userId` asc (deterministic) — the
 *     tie-break only affects RENDER ORDER, not rank.
 *   - DENSE RANKING: tied centi-MP rows share a rank; the next distinct
 *     centi-MP gets `prevRank + 1`. Example: `[1300, 633, 633, 566]` →
 *     ranks `[1, 2, 2, 3]` (NOT competition-style `[1, 2, 2, 4]`). This
 *     keeps the last visible rank aligned with the total member count
 *     in small groups (e.g. a 13-person group's bottom rank is 13, not
 *     14+) and prevents the minimized 3-row view from rendering ranks
 *     that don't match the row count above the viewer.
 *   - No "gap from opt-out" — ranks are computed AFTER the opt-out
 *     filter, so hidden users never leave a missing position.
 *   - The requester's own row appears in `ranking[]` with their dense
 *     rank within their own view (filtered + self). The `requestor`
 *     field mirrors the same row for client convenience.
 *   - `mp` in the response is DISPLAY MP (centi-MP / 100, Math.round).
 */
export function buildLeaderboard(
  rows: readonly SGScore[],
  members: readonly SGMember[],
  requesterUserId: string,
  period: LeaderboardPeriod,
  nowUtcISO: string,
): LeaderboardResponse {
  const rowByUser = new Map<string, SGScore>();
  for (const r of rows) rowByUser.set(r.userId, r);

  type Joined = {
    member: SGMember;
    centi: number;
    timeMs: number;
    streak: number;
    lastStudyLocalDayISO: string;
  };

  const alive = members.filter((m) => !m.deletedTimeISO);

  // UTC-day approximation for the "today" frame — we don't know each
  // member's TZ. Worst-case slop is ±1 day for users far from UTC, which
  // resolves on their next delta (server uses their real userLocalDayISO).
  const todayLocalDayISO = nowUtcISO.slice(0, 10);

  const joined: Joined[] = alive.map((member) => {
    const row = rowByUser.get(member.userId);
    return {
      member,
      centi: row ? computePeriodMP(row, period, nowUtcISO) : 0,
      timeMs: row ? computePeriodTimeMs(row, period, nowUtcISO) : 0,
      streak: effectiveStreak(row, todayLocalDayISO),
      lastStudyLocalDayISO: row?.lastStudyLocalDayISO ?? '',
    };
  });

  joined.sort((a, b) => {
    if (b.centi !== a.centi) return b.centi - a.centi;
    if (b.lastStudyLocalDayISO !== a.lastStudyLocalDayISO) {
      return b.lastStudyLocalDayISO.localeCompare(a.lastStudyLocalDayISO);
    }
    return a.member.userId.localeCompare(b.member.userId);
  });

  const filtered = joined.filter(
    (j) => j.member.leaderboardOptOut !== true || j.member.userId === requesterUserId,
  );

  // Independent Time ranking over the SAME visible set — sorted by
  // `timeMs` desc with the identical tie-breaks and the identical dense
  // semantics as the MP ranking below. Computed as a side map (userId →
  // dense time rank) so `ranking[]` keeps its MP order on the wire; the
  // Time tab re-sorts client-side by `timeRank`.
  const timeOrdered = [...filtered].sort((a, b) => {
    if (b.timeMs !== a.timeMs) return b.timeMs - a.timeMs;
    if (b.lastStudyLocalDayISO !== a.lastStudyLocalDayISO) {
      return b.lastStudyLocalDayISO.localeCompare(a.lastStudyLocalDayISO);
    }
    return a.member.userId.localeCompare(b.member.userId);
  });
  const timeRankByUser = new Map<string, number>();
  let lastTimeMs: number | null = null;
  let lastTimeRank = 0;
  for (const j of timeOrdered) {
    if (lastTimeMs === null || j.timeMs !== lastTimeMs) {
      lastTimeRank += 1;
      lastTimeMs = j.timeMs;
    }
    timeRankByUser.set(j.member.userId, lastTimeRank);
  }

  // Dense ranking — tied centi-MP rows share a rank; the NEXT distinct
  // centi-MP gets `prevRank + 1` (NOT the count-of-rows-above-it that
  // standard-competition ranking emits). Chosen because:
  //   - The minimized 3-row view picks slices like #1/#k-1/#k where
  //     the user's rank `k` is rendered alongside their neighbour; if
  //     ties above the user inflated `k` (competition jumps), the
  //     viewer's "you're #12" reads differently from the visible row
  //     count, which is confusing for a small-group leaderboard.
  //   - The total members count and the largest rank stay aligned —
  //     in a 13-person group, the bottom rank is `13`, not `14+`.
  const ranking: LeaderboardRow[] = [];
  let lastCenti: number | null = null;
  let lastRank = 0;
  for (const j of filtered) {
    if (lastCenti === null || j.centi !== lastCenti) {
      lastRank += 1;
      lastCenti = j.centi;
    }
    ranking.push({
      userId: j.member.userId,
      rank: lastRank,
      mp: Math.round(j.centi / 100),
      streak: j.streak,
      hiddenSelf:
        j.member.userId === requesterUserId && j.member.leaderboardOptOut === true,
      displayName: j.member.displayName,
      avatarUrl: j.member.avatarUrl,
      lastActive: j.member.lastOpenedAtISO,
      timeMs: j.timeMs,
      timeRank: timeRankByUser.get(j.member.userId) ?? 0,
    });
  }

  const requestor = ranking.find((r) => r.userId === requesterUserId) ?? {
    // Defensive — REST endpoint already enforces membership, so this
    // branch shouldn't fire in production. Returning a zeroed row keeps
    // the response shape stable for any caller that consumes the type.
    userId: requesterUserId,
    rank: 0,
    mp: 0,
    streak: 0,
    hiddenSelf: false,
    timeMs: 0,
    timeRank: 0,
  };

  return { period, ranking, requestor };
}

// ---------------------------------------------------------------------------
// CLIENT-SIDE score formula — used by the study-session integration to
// turn (eventType, currentStreak) into the centi-MP integer that the
// client pushes over `sg-score-delta`. The server accepts that integer
// verbatim and increments storage by it; no per-event rounding ever
// happens server-side. That preserves the invariant "round on aggregate,
// never per-event" — the only display rounding happens once per bucket
// inside `buildLeaderboard`.
//
// Lives in the SAME file as the server-shared period math (above) so a
// future reader sees one canonical home for "how a leaderboard number
// is shaped" — client centi-MP math + server period bucketing share a
// module instead of drifting in separate files.
// ---------------------------------------------------------------------------

/**
 * The 10 scoring event types. MUST match the underscored enum in
 * `sg-score-wire.ts` one-for-one — quiz vs exam vs oral_exam_session
 * vs case_study_session stay distinct so the server can authoritatively
 * re-verify `delta` once anti-cheat ships, and per-mode analytics can
 * land later without a wire migration. That re-verification is why the
 * event type, never a separate flag, is what carries correctness: the
 * type alone has to determine the points.
 *
 * `sr_review` is LEGACY and must not be sent by new code — it is the
 * grade-blind spaced-repetition event that paid 3 MP whether the card
 * came back "Impossible" or "Easy". Clients now send `sr_correct` /
 * `sr_wrong`, but old app builds keep pushing `sr_review` for as long
 * as they are installed, so the server must go on accepting it.
 *
 * `oral_exam_session` and `case_study_session` are the PER-SESSION
 * variants — they bypass `EVENT_POINTS` (which is per-card display MP)
 * and use `scoreForOralExamSession` / `scoreForCaseStudySession`
 * instead, since their base points scale with the score percent rather
 * than being a fixed integer.
 */
export type ScoreEventType =
  | 'sr_review'
  | 'sr_correct'
  | 'sr_wrong'
  | 'fr_review'
  | 'quiz_correct'
  | 'quiz_wrong'
  | 'exam_correct'
  | 'exam_wrong'
  | 'oral_exam_session'
  | 'case_study_session';

/**
 * Base MP per event (display MP, not centi-MP — `scoreFor` multiplies
 * by the streak multiplier and converts to centi-MP at the end).
 *
 * `Readonly<Record<...>>` guarantees compile-time exhaustiveness: a new
 * `ScoreEventType` variant that forgets to add an entry here is a TS
 * error, not a silent zero.
 *
 * A COMPLETED learning item is worth 3, a missed one 1 — the same 3/1
 * split across every per-card mode, so the reward tracks "did you know
 * it", not "did you show up". `sr_review` keeps the old flat 3 because
 * it is the legacy grade-blind event: re-scoring it would silently
 * re-price whatever old app builds are still sending.
 *
 * `oral_exam_session` / `case_study_session` carry `0` here — their
 * base points are derived dynamically from the session's score percent
 * (`scoreForOralExamSession` / `scoreForCaseStudySession`), not a fixed
 * per-event value, so the per-card `scoreFor` path must never be used
 * for them. The `0` entries keep the exhaustiveness check honest
 * without inviting accidental use.
 */
export const EVENT_POINTS: Readonly<Record<ScoreEventType, number>> = {
  sr_review: 3,
  sr_correct: 3,
  sr_wrong: 1,
  fr_review: 1,
  quiz_correct: 3,
  quiz_wrong: 1,
  exam_correct: 3,
  exam_wrong: 1,
  oral_exam_session: 0,
  case_study_session: 0,
};

/**
 * Display-MP pool awarded for a perfect oral-exam session (100%). The
 * actual eventPoints are `ORAL_EXAM_SESSION_POOL_MP * (percent / 100)`,
 * then multiplied by the group's streak multiplier and rounded to
 * centi-MP. Public constant so a future tuning pass + tests share one
 * source of truth.
 */
export const ORAL_EXAM_SESSION_POOL_MP = 50;

/**
 * Display-MP pool awarded for a perfect case-study session (100%).
 * Same shape as {@link ORAL_EXAM_SESSION_POOL_MP} — a case study is
 * one comparable "unit of effort" to an oral exam (prep + a single
 * multi-part answer graded end-to-end), so their pools intentionally
 * match. Kept as its own constant so a future tuning pass can diverge
 * without touching oral-exam callers.
 */
export const CASE_STUDY_SESSION_POOL_MP = 50;

/**
 * Streak multiplier bands. Contiguous, `max` inclusive; the last
 * band's `max` is `+Infinity` so any streak ≥ 30 lands on `1.7`.
 * `multiplierForStreak` walks this table in order.
 */
export const STREAK_MULTIPLIER_TABLE: ReadonlyArray<{
  min: number;
  max: number;
  mult: number;
}> = [
  { min: 1, max: 1, mult: 1.0 },
  // Day-2 silent +5% bump — reinforces the early habit loop without
  // triggering the streak-active toast (threshold 1.5), so the user
  // gets a quiet reward for showing up the day after onboarding
  // before the louder Day-7 milestone kicks in.
  { min: 2, max: 2, mult: 1.05 },
  { min: 3, max: 4, mult: 1.15 },
  { min: 5, max: 6, mult: 1.3 },
  { min: 7, max: 13, mult: 1.5 },
  { min: 14, max: 29, mult: 1.6 },
  { min: 30, max: Number.POSITIVE_INFINITY, mult: 1.7 },
];

export type ScoreBreakdown = {
  /** Base MP from EVENT_POINTS (display MP — NOT centi-MP). */
  eventPoints: number;
  /** Multiplier applied (NOT centi-MP). */
  multiplier: number;
  /**
   * Wire payload — centi-MP integer the client pushes over
   * `sg-score-delta`. Integer by construction (`Math.round` applied
   * after multiplication), so the wire schema (`z.number().int()` in
   * `sg-score-wire.ts`) accepts it as-is.
   */
  centiMP: number;
};

export function eventPointsFor(eventType: ScoreEventType): number {
  return EVENT_POINTS[eventType];
}

/**
 * Multiplier for a streak day count. Defensive at the lower edge —
 * `streak <= 0` returns `1.0`. The group streak STARTS at 1 on the
 * first delta of a brand-new user (server-side, in
 * `score-delta-handler.ts`), so in practice this branch only fires for
 * the pre-first-ack moment when the client hasn't yet learned its
 * server-authoritative streak.
 */
export function multiplierForStreak(streak: number): number {
  if (streak <= 0) return 1.0;
  for (const band of STREAK_MULTIPLIER_TABLE) {
    if (streak >= band.min && streak <= band.max) return band.mult;
  }
  // Unreachable by construction (the last band's `max` is `+Infinity`).
  return 1.0;
}

/**
 * Centi-MP delta for a single card given the event type and the
 * pre-card group streak. Returns the breakdown so callers (toast
 * logic, summary breakdown popover) can show the multiplier separately
 * from the integer that actually went over the wire.
 *
 * `centiMP` is the integer the client pushes verbatim — the
 * "round on aggregate, never per-event" invariant holds because the
 * server's storage arithmetic NEVER re-rounds in centi-MP, and the only
 * display rounding (`Math.round(centi / 100)`) happens once per bucket
 * downstream in `buildLeaderboard` above (REST) or web UI consumers.
 */
export function scoreFor(eventType: ScoreEventType, streak: number): ScoreBreakdown {
  const eventPoints = eventPointsFor(eventType);
  const multiplier = multiplierForStreak(streak);
  const centiMP = Math.round(eventPoints * multiplier * 100);
  return { eventPoints, multiplier, centiMP };
}

/**
 * Per-SESSION centi-MP delta for one finished oral exam.
 *
 * Formula: `eventPoints = ORAL_EXAM_SESSION_POOL_MP * (percent / 100)`,
 * then `centiMP = Math.round(eventPoints * multiplier * 100)`. So a
 * perfect 100% session at streak ≥ 30 (multiplier 1.7) credits
 * `50 * 1.7 = 85 display MP` (centi 8500); a 49% session at streak 1
 * (multiplier 1.0) credits `Math.round(24.5) = 25 display MP` (centi
 * 2450). The `Math.round(centi)` step preserves the "round on aggregate,
 * never per-event" invariant — the round only ever lands on the wire
 * integer, never mid-aggregate, identical to `scoreFor` above.
 *
 * Defensive at both edges of `percent`: clamped to `[0, 100]` so a
 * malformed report can't push a negative or > pool delta over the wire
 * (the wire schema rejects negatives with `nonnegative()`, but better
 * to clamp at the formula boundary so the call site doesn't have to
 * pre-validate).
 */
export function scoreForOralExamSession(percent: number, streak: number): ScoreBreakdown {
  const clamped = Math.max(0, Math.min(100, percent));
  const eventPoints = ORAL_EXAM_SESSION_POOL_MP * (clamped / 100);
  const multiplier = multiplierForStreak(streak);
  const centiMP = Math.round(eventPoints * multiplier * 100);
  return { eventPoints, multiplier, centiMP };
}

/**
 * Per-SESSION centi-MP delta for one finished case study. Sibling of
 * {@link scoreForOralExamSession} — same shape, same clamping, same
 * multiplier table; only the pool constant differs (kept as its own
 * knob for future tuning). Callers push the returned `centiMP` on the
 * `sg-score-delta` wire with `eventType: 'case_study_session'`.
 */
export function scoreForCaseStudySession(
  percent: number,
  streak: number,
): ScoreBreakdown {
  const clamped = Math.max(0, Math.min(100, percent));
  const eventPoints = CASE_STUDY_SESSION_POOL_MP * (clamped / 100);
  const multiplier = multiplierForStreak(streak);
  const centiMP = Math.round(eventPoints * multiplier * 100);
  return { eventPoints, multiplier, centiMP };
}
